// WhatYouToldUsZigzag.js
import React from "react";

const defaultQuotes = [
  "“Our systems are packed with data, but we still can't answer basic questions.”",
  "“No one fully trusts the numbers — every report turns into a debate.”",
  "“We spend more time fixing data than using it to make decisions.”",
  "“Every transformation project slows down because of data issues.”",
];

const defaultImpacts = [
  "Delayed projects and launches",
  "Lost revenue opportunities",
  "Eroded stakeholder confidence",
  "Support tickets and rework spike",
  "SLA breaches and penalties",
  "Fire-drill culture becomes the norm",
];

function Pin() {
  return (
    <span className="absolute -top-2 -left-2 h-4 w-4 rounded-full bg-blue-500 shadow-[0_2px_8px_rgba(37,99,235,0.45)] ring-2 ring-white" />
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50/60 px-3 py-1 text-xs font-medium text-slate-700 backdrop-blur-md shadow-sm">
      {/* tiny check svg */}
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="mr-1.5 h-3.5 w-3.5 text-blue-600"
      >
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0L3.293 9.957a1 1 0 011.414-1.414l3.04 3.04 6.543-6.543a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
      {children}
    </span>
  );
}

export default function WhatYouToldUsZigzag({
  quotes = defaultQuotes,
  impacts = defaultImpacts,
  title = "Your data is everywhere… but not working for you",
  subtitle = "Leaders in manufacturing, FMCG, recruitment, finance, retail, healthcare, logistics, and the public sector keep saying the same thing: messy data, conflicting reports, and decisions made on shaky foundations. Different industries, same story — if your organisation runs on more than one system, this is already true for you.",
}) {
  return (
    <section
      id="problem"
      className="relative py-20 bg-gradient-to-b from-blue-50 via-white to-white"
    >
      {/* soft backdrop glow */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-0 h-64 w-[80rem] -translate-x-1/2 rounded-[6rem] bg-[radial-gradient(42rem_16rem_at_50%_0%,rgba(96,165,250,0.18),transparent)]" />
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="mt-4 text-3xl font-semibold text-slate-900 sm:text-4xl">
            {title}
          </h2>
          <p className="mt-3 text-slate-600">{subtitle}</p>
        </div>

        {/* Zigzag quotes */}
        <div className="mx-auto mt-12 max-w-4xl space-y-8">
          {quotes.map((q, i) => {
            const rotate = i % 2 === 0 ? "rotate-[5deg]" : "-rotate-[5deg]";
            const align =
              i % 2 === 0 ? "ml-0 mr-8 sm:mr-20" : "ml-8 sm:ml-20 mr-0";
            return (
              <div
                key={i}
                className={`relative ${rotate} ${align} transition-transform duration-300 will-change-transform hover:rotate-0`}
              >
                <Pin />
                <div className="rounded-3xl border border-blue-200 bg-white/70 p-8 shadow-xl backdrop-blur-md">
                  <blockquote className="text-2xl sm:text-3xl md:text-5xl leading-tight font-semibold tracking-tight">
                    <span className="bg-gradient-to-r from-blue-700 via-sky-500 to-cyan-500 bg-clip-text text-transparent drop-shadow-[0_1px_0_rgba(255,255,255,0.7)]">
                      {q}
                    </span>
                  </blockquote>
                </div>
              </div>
            );
          })}
        </div>

        {/* Impacts on the business (optional, soft list under quotes) */}
        {impacts && impacts.length > 0 && (
          <div className="mx-auto mt-10 max-w-4xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
              What this looks like in the business:
            </p>
            <div className="flex flex-wrap gap-2">
              {impacts.map((impact, i) => (
                <Pill key={i}>{impact}</Pill>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}